import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageLayout } from '@voilajsx/uikit/page';
import { Card, CardContent, CardHeader, CardTitle } from '@voilajsx/uikit/card';
import { Button } from '@voilajsx/uikit/button';
import { Badge } from '@voilajsx/uikit/badge';
import { Alert, AlertDescription } from '@voilajsx/uikit/alert';
import { Input } from '@voilajsx/uikit/input';
import { Label } from '@voilajsx/uikit/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@voilajsx/uikit/select';
import {
  ArrowLeft, AlertTriangle, Mail, Phone, Calendar, Shield, Edit,
  Coins, Download, FileText, Plus, Minus, Loader2, Eye, Clock,
  CreditCard, RefreshCw
} from 'lucide-react';
import { Header, SEO } from '../../../../shared/components';
import { AdminSidebar } from '../../../admin/components/AdminSidebar';
import { useAuth } from '../../../auth';
import { route, hasRole } from '../../../../shared/utils';
import { config } from '../../../auth/config';

interface UserData {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  role: string;
  level: string;
  tenantId: string | null;
  isVerified: boolean;
  isActive: boolean;
  lastLogin: string | null;
  createdAt: string;
  updatedAt: string;
}

const GOLD = '#FFD93D';

const getRoleName = (role: string, level: string) => {
  if (role === 'admin' && level === 'system') return 'System Admin';
  if (role === 'moderator' && level === 'manage') return 'Moderator';
  return 'User';
};

const getRoleStyle = (role: string, level: string) => {
  if (role === 'admin') return { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)' };
  if (role === 'moderator') return { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)' };
  return { color: '#a1a1aa', bg: 'rgba(161,161,170,0.1)', border: 'rgba(161,161,170,0.25)' };
};

const timeAgo = (dateStr: string): string => {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ShowUserPage: React.FC = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);

  // Tabs
  const [activeTab, setActiveTab] = useState<'profile' | 'credits' | 'activity'>('profile');

  // Password
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({ newPassword: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);

  // Credits
  const [balance, setBalance] = useState<number | null>(null);
  const [totalAllocated, setTotalAllocated] = useState(0);
  const [totalUsed, setTotalUsed] = useState(0);
  const [ledger, setLedger] = useState<any[]>([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [grantMode, setGrantMode] = useState<'grant' | 'revoke'>('grant');
  const [grantAmount, setGrantAmount] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantSuccess, setGrantSuccess] = useState<string | null>(null);
  const [showGrantPopup, setShowGrantPopup] = useState(false);

  // Activity
  const [downloads, setDownloads] = useState<any[]>([]);
  const [designs, setDesigns] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);

  const userId = searchParams.get('id');
  const canViewUsers = user ? hasRole(user, ['moderator.review', 'moderator.approve', 'moderator.manage', 'admin.tenant', 'admin.org', 'admin.system']) : false;
  const canManageUsers = user ? hasRole(user, ['admin.tenant', 'admin.org', 'admin.system']) : false;
  const isAdminSystem = user?.role === 'admin' && user?.level === 'system';

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    [config.auth.headers.frontendKey]: config.auth.headers.frontendKeyValue,
    [config.auth.headers.auth]: `Bearer ${token}`,
  });

  const fetchUser = async () => {
    if (!userId || !token) return;
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/user/admin/users/${userId}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      setUserData(data.user);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch user');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCredits = async () => {
    if (!userId || !token) return;
    setLedgerLoading(true);
    try {
      const [ledgerRes] = await Promise.all([
        fetch(`${config.api.baseUrl}/api/credit/admin/ledger?userId=${userId}&pageSize=50`, { headers: authHeaders() }),
      ]);
      if (ledgerRes.ok) {
        const data = await ledgerRes.json();
        const entries = data.data?.entries || [];
        setLedger(entries);
        if (entries.length > 0) setBalance(entries[0].balanceAfter);
        // Compute totals
        let allocated = 0, used = 0;
        for (const e of entries) {
          if (e.amount > 0) allocated += e.amount;
          else used += Math.abs(e.amount);
        }
        setTotalAllocated(allocated);
        setTotalUsed(used);
      }
    } catch {} finally {
      setLedgerLoading(false);
    }
  };

  const fetchActivity = async () => {
    if (!userId || !token) return;
    setActivityLoading(true);
    try {
      // Fetch audit logs for this user's downloads
      const res = await fetch(`${config.api.baseUrl}/api/audit/admin/list?actorId=${userId}&pageSize=50`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        const logs = data.data?.entries || data.data?.logs || data.data || [];
        setDownloads(logs.filter((l: any) => l.entityType === 'download' || l.action?.includes('download')));
        setDesigns(logs.filter((l: any) => l.entityType === 'design'));
      }
    } catch {} finally {
      setActivityLoading(false);
    }
  };

  const handleGrant = async () => {
    if (!grantAmount || !grantReason || !userId) return;
    setGrantLoading(true);
    setGrantSuccess(null);
    try {
      const endpoint = grantMode === 'grant' ? '/api/credit/admin/grant' : '/api/credit/admin/revoke';
      const res = await fetch(`${config.api.baseUrl}${endpoint}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ userId, amount: Number(grantAmount), reason: grantReason }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || 'Failed'); }
      setGrantSuccess(`${grantMode === 'grant' ? 'Granted' : 'Revoked'} ₹${grantAmount}`);
      setGrantAmount(''); setGrantReason('');
      setShowGrantPopup(false);
      fetchCredits();
      setTimeout(() => setGrantSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGrantLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordData.newPassword || passwordData.newPassword.length < 8) return;
    setPasswordLoading(true);
    try {
      const res = await fetch(`${config.api.baseUrl}/api/user/admin/users/${userId}/password`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ newPassword: passwordData.newPassword }),
      });
      if (!res.ok) throw new Error('Failed to update password');
      setPasswordSuccess('Password updated');
      setPasswordData({ newPassword: '' }); setShowPasswordForm(false);
      setTimeout(() => setPasswordSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setPasswordLoading(false);
    }
  };

  useEffect(() => {
    if (userId && token && user) fetchUser();
  }, [userId, token, user]);

  useEffect(() => {
    if (activeTab === 'credits' && userId && token) fetchCredits();
    if (activeTab === 'activity' && userId && token) fetchActivity();
  }, [activeTab, userId, token]);

  if (!user) return null;

  if (!canViewUsers || !userId) {
    return (
      <PageLayout size="full"><SEO title="Access Denied" /><Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md"><CardContent className="pt-6">
              <div className="text-center">
                <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
                <h2 className="text-lg font-semibold mb-2">Access Denied</h2>
                <Button asChild><a href={route('/user/admin')}>Back</a></Button>
              </div>
            </CardContent></Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  if (isLoading) {
    return (
      <PageLayout size="full"><SEO title="Loading..." /><Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  if (error && !userData) {
    return (
      <PageLayout size="full"><SEO title="Error" /><Header />
        <PageLayout.Content>
          <div className="flex items-center justify-center min-h-96">
            <Card className="w-full max-w-md"><CardContent className="pt-6 text-center">
              <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
              <p className="text-muted-foreground mb-4">{error}</p>
              <div className="flex gap-2 justify-center">
                <Button onClick={fetchUser}>Retry</Button>
                <Button variant="outline" asChild><a href={route('/user/admin')}>Back</a></Button>
              </div>
            </CardContent></Card>
          </div>
        </PageLayout.Content>
      </PageLayout>
    );
  }

  if (!userData) return null;

  const roleStyle = getRoleStyle(userData.role, userData.level);
  const roleName = getRoleName(userData.role, userData.level);

  const TABS = [
    { key: 'profile' as const, label: 'Profile', icon: Shield },
    { key: 'credits' as const, label: 'Credits', icon: Coins },
    { key: 'activity' as const, label: 'Activity', icon: Clock },
  ];

  const TYPE_LABELS: Record<string, string> = {
    signup_bonus: 'Signup Bonus', purchase: 'Purchase', download_debit: 'Download',
    admin_grant: 'Admin Grant', admin_revoke: 'Admin Revoke', refund: 'Refund',
  };

  return (
    <PageLayout size="full">
      <SEO title={`${userData.name || userData.email} - User Details`} />
      <Header />

      <PageLayout.Content>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <AdminSidebar />
          <div style={{ flex: 1, minWidth: 0, paddingBottom: 72 }}>
          {/* Back + Actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <a href={route('/user/admin')} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#888', textDecoration: 'none', fontSize: 13 }}>
              <ArrowLeft style={{ width: 16, height: 16 }} /> Back to Users
            </a>
            {canManageUsers && (
              <div style={{ display: 'flex', gap: 8 }}>
                {isAdminSystem && (
                  <Button variant="outline" size="sm" className="gap-2" onClick={() => { fetchCredits(); setShowGrantPopup(true); }}>
                    <Coins className="h-4 w-4" /> Credits
                  </Button>
                )}
                {(userData.role !== 'admin' || userData.level !== 'system') && (
                  <>
                    <Button variant="outline" size="sm" className="gap-2" onClick={() => setShowPasswordForm(true)}>
                      <RefreshCw className="h-4 w-4" /> Password
                    </Button>
                    <Button asChild size="sm" className="gap-2">
                      <a href={route(`/user/admin/edit?id=${userData.id}`)}>
                        <Edit className="h-4 w-4" /> Edit User
                      </a>
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Password Change Popup */}
          {showPasswordForm && canManageUsers && (
            <>
              <div onClick={() => setShowPasswordForm(false)} style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999,
                backdropFilter: 'blur(4px)',
              }} />
              <div style={{
                position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                background: '#141414', border: '1px solid #2a2a2a', borderRadius: 14,
                width: '100%', maxWidth: 420, zIndex: 1000, padding: '24px',
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>Change Password</h3>
                <p style={{ fontSize: 13, color: '#888', margin: '0 0 20px' }}>
                  Set a new password for <span style={{ color: '#ccc', fontWeight: 600 }}>{userData.name || userData.email}</span>
                </p>

                <form onSubmit={handlePasswordSubmit}>
                  <div style={{ marginBottom: 12 }}>
                    <Label className="text-xs text-zinc-400 mb-1 block">New Password</Label>
                    <Input
                      type="text" value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({ newPassword: e.target.value })}
                      placeholder="Min 8 characters"
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
                    <Button type="button" variant="outline" size="sm" onClick={() => {
                      const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
                      setPasswordData({ newPassword: Array.from({ length: 12 }, () => c[Math.floor(Math.random() * c.length)]).join('') });
                    }}>Random</Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setPasswordData({ newPassword: 'default12345' })}>
                      Default
                    </Button>
                    {userData.phone && userData.phone.length >= 8 && (
                      <Button type="button" variant="outline" size="sm" onClick={() => setPasswordData({ newPassword: userData!.phone || '' })}>
                        Phone
                      </Button>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button
                      type="button" onClick={() => setShowPasswordForm(false)}
                      style={{
                        padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                        background: '#222', border: '1px solid #333', color: '#999', cursor: 'pointer',
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={passwordLoading || passwordData.newPassword.length < 8}
                      style={{
                        padding: '8px 24px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                        background: passwordData.newPassword.length >= 8 ? GOLD : '#333',
                        border: 'none', color: passwordData.newPassword.length >= 8 ? '#111' : '#666',
                        cursor: passwordData.newPassword.length >= 8 ? 'pointer' : 'not-allowed',
                      }}
                    >
                      {passwordLoading ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}

          {/* Grant/Revoke Credits Popup */}
          {showGrantPopup && isAdminSystem && (
            <>
              <style>{`[data-radix-popper-content-wrapper] { z-index: 1100 !important; }`}</style>
              <div onClick={() => setShowGrantPopup(false)} style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999,
                backdropFilter: 'blur(4px)',
              }} />
              <div style={{
                position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                background: '#141414', border: '1px solid #2a2a2a', borderRadius: 14,
                width: '100%', maxWidth: 440, zIndex: 1000, padding: '24px',
              }}>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>
                  {grantMode === 'grant' ? 'Grant' : 'Revoke'} Credits
                </h3>
                <p style={{ fontSize: 13, color: '#888', margin: '0 0 20px' }}>
                  {grantMode === 'grant' ? 'Add credits to' : 'Remove credits from'}{' '}
                  <span style={{ color: '#ccc', fontWeight: 600 }}>{userData.name || userData.email}</span>
                  {balance !== null && (
                    <span style={{ color: '#555' }}> · Current balance: <span style={{ color: GOLD, fontWeight: 600 }}>₹{balance}</span></span>
                  )}
                </p>

                {/* Mode toggle */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
                  {(['grant', 'revoke'] as const).map(mode => (
                    <button key={mode} onClick={() => setGrantMode(mode)} style={{
                      padding: '6px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                      border: grantMode === mode ? `1px solid ${mode === 'grant' ? GOLD : '#ef4444'}` : '1px solid #333',
                      background: grantMode === mode ? (mode === 'grant' ? 'rgba(255,217,61,0.1)' : 'rgba(239,68,68,0.1)') : 'transparent',
                      color: grantMode === mode ? (mode === 'grant' ? GOLD : '#ef4444') : '#666',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                      {mode === 'grant' ? <Plus size={13} /> : <Minus size={13} />}
                      {mode === 'grant' ? 'Grant' : 'Revoke'}
                    </button>
                  ))}
                </div>

                <div style={{ marginBottom: 12 }}>
                  <Label className="text-xs text-zinc-400 mb-1 block">Amount (₹)</Label>
                  <Input type="number" min="1" placeholder="e.g. 100" value={grantAmount}
                    onChange={(e) => setGrantAmount(e.target.value)} />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <Label className="text-xs text-zinc-400 mb-1 block">Reason</Label>
                  <Select value={grantReason} onValueChange={setGrantReason}>
                    <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                    <SelectContent>
                      {grantMode === 'grant' ? (
                        <>
                          <SelectItem value="Admin grant">Admin grant</SelectItem>
                          <SelectItem value="Complimentary credits">Complimentary credits</SelectItem>
                          <SelectItem value="Refund for failed download">Refund for failed download</SelectItem>
                          <SelectItem value="Promotional offer">Promotional offer</SelectItem>
                          <SelectItem value="Support resolution">Support resolution</SelectItem>
                        </>
                      ) : (
                        <>
                          <SelectItem value="Policy violation">Policy violation</SelectItem>
                          <SelectItem value="Duplicate credit entry">Duplicate credit entry</SelectItem>
                          <SelectItem value="Account correction">Account correction</SelectItem>
                          <SelectItem value="Refund reversal">Refund reversal</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setShowGrantPopup(false); setGrantAmount(''); setGrantReason(''); }}
                    style={{
                      padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      background: '#222', border: '1px solid #333', color: '#999', cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGrant}
                    disabled={grantLoading || !grantAmount || !grantReason}
                    style={{
                      padding: '8px 24px', borderRadius: 8, fontSize: 13, fontWeight: 700,
                      background: grantAmount && grantReason
                        ? (grantMode === 'grant' ? GOLD : '#ef4444')
                        : '#333',
                      border: 'none',
                      color: grantAmount && grantReason
                        ? (grantMode === 'grant' ? '#111' : '#fff')
                        : '#666',
                      cursor: grantAmount && grantReason ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {grantLoading ? 'Processing...' : grantMode === 'grant' ? 'Grant Credits' : 'Revoke Credits'}
                  </button>
                </div>
              </div>
            </>
          )}

          {/* User Header Card */}
          <div style={{
            background: '#1a1a1a', border: '1px solid #222', borderRadius: 14,
            padding: '24px 28px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 20,
          }}>
            {/* Avatar */}
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              background: roleStyle.bg, border: `2px solid ${roleStyle.border}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 26, fontWeight: 700, color: roleStyle.color, flexShrink: 0,
            }}>
              {(userData.name || userData.email)[0].toUpperCase()}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, color: '#fff', margin: 0 }}>
                  {userData.name || 'Unnamed'}
                </h1>
                <span style={{
                  fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 5,
                  background: roleStyle.bg, color: roleStyle.color, border: `1px solid ${roleStyle.border}`,
                }}>
                  {roleName}
                </span>
                <Badge variant={userData.isActive ? 'default' : 'destructive'} className="text-xs">
                  {userData.isActive ? 'Active' : 'Inactive'}
                </Badge>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, color: '#888', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Mail style={{ width: 13, height: 13 }} /> {userData.email}
                </span>
                {userData.phone && (
                  <span style={{ fontSize: 13, color: '#777', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Phone style={{ width: 13, height: 13 }} /> {userData.phone}
                  </span>
                )}
                <span style={{ fontSize: 12, color: '#555' }}>
                  Joined {timeAgo(userData.createdAt)}
                </span>
                <span style={{ fontSize: 12, color: '#444' }}>
                  Last login: {userData.lastLogin ? timeAgo(userData.lastLogin) : 'Never'}
                </span>
              </div>
            </div>
          </div>

          {/* Success/Error alerts */}
          {passwordSuccess && (
            <Alert className="mb-4 bg-green-950 border-green-800 text-green-300">
              <AlertDescription>{passwordSuccess}</AlertDescription>
            </Alert>
          )}
          {error && (
            <Alert className="mb-4 bg-destructive/10 border-destructive text-destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #222', paddingBottom: 0 }}>
            {TABS.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    padding: '10px 18px', fontSize: 13, fontWeight: 600,
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: isActive ? GOLD : '#666',
                    borderBottom: isActive ? `2px solid ${GOLD}` : '2px solid transparent',
                    display: 'flex', alignItems: 'center', gap: 6,
                    transition: 'all 0.15s', marginBottom: -1,
                  }}
                >
                  <Icon style={{ width: 15, height: 15 }} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* ─── Profile Tab ──────────────────────────── */}
          {activeTab === 'profile' && (
            <div className="space-y-5">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {/* Info */}
                <div style={{ background: '#1a1a1a', border: '1px solid #222', borderRadius: 10, padding: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#999', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Account Info</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {[
                      ['Email', userData.email],
                      ['Phone', userData.phone || '—'],
                      ['Role', roleName],
                      ['Status', userData.isActive ? 'Active' : 'Inactive'],
                      ['Verified', userData.isVerified ? 'Yes' : 'No'],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 13, color: '#666' }}>{label}</span>
                        <span style={{ fontSize: 13, color: '#ccc', fontWeight: 500 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Timestamps */}
                <div style={{ background: '#1a1a1a', border: '1px solid #222', borderRadius: 10, padding: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#999', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Timestamps</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {[
                      ['Created', new Date(userData.createdAt).toLocaleString('en-IN')],
                      ['Last Updated', new Date(userData.updatedAt).toLocaleString('en-IN')],
                      ['Last Login', userData.lastLogin ? new Date(userData.lastLogin).toLocaleString('en-IN') : 'Never'],
                    ].map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 13, color: '#666' }}>{label}</span>
                        <span style={{ fontSize: 13, color: '#ccc', fontWeight: 500 }}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* ─── Credits Tab ──────────────────────────── */}
          {activeTab === 'credits' && (
            <div className="space-y-5">
              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                {[
                  { label: 'Total Allocated', value: totalAllocated, color: '#22c55e', icon: Plus },
                  { label: 'Total Used', value: totalUsed, color: '#ef4444', icon: Minus },
                  { label: 'Balance', value: balance ?? 0, color: GOLD, icon: Coins },
                ].map(stat => {
                  const StatIcon = stat.icon;
                  return (
                    <div key={stat.label} style={{
                      background: '#1a1a1a', border: '1px solid #222', borderRadius: 10, padding: '18px 20px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{
                          width: 30, height: 30, borderRadius: '50%',
                          background: `${stat.color}18`, border: `1px solid ${stat.color}40`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <StatIcon style={{ width: 14, height: 14, color: stat.color }} />
                        </div>
                        <span style={{ fontSize: 11, color: '#666', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {stat.label}
                        </span>
                      </div>
                      <p style={{ fontSize: 24, fontWeight: 700, color: stat.color, margin: 0 }}>
                        {ledgerLoading ? '...' : `₹${stat.value}`}
                      </p>
                    </div>
                  );
                })}
              </div>

              {grantSuccess && (
                <Alert className="bg-green-950 border-green-800 text-green-300">
                  <AlertDescription>{grantSuccess}</AlertDescription>
                </Alert>
              )}

              {/* Ledger */}
              <div style={{ background: '#1a1a1a', border: '1px solid #222', borderRadius: 10, padding: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#999', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Transaction History
                </h3>
                {ledgerLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="h-10 rounded-md bg-zinc-800 animate-pulse" />
                    ))}
                  </div>
                ) : ledger.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#555', textAlign: 'center', padding: 24 }}>No transactions found</p>
                ) : (
                  <div className="space-y-2">
                    {ledger.map((entry: any) => (
                      <div key={entry.id} style={{
                        display: 'flex', alignItems: 'center', gap: 12,
                        padding: '10px 14px', background: '#151515', borderRadius: 8, border: '1px solid #1a1a1a',
                      }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                          background: entry.amount > 0 ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)',
                          border: `1px solid ${entry.amount > 0 ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          {entry.amount > 0
                            ? <Plus style={{ width: 14, height: 14, color: '#22c55e' }} />
                            : <Minus style={{ width: 14, height: 14, color: '#ef4444' }} />}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#ccc' }}>
                              {TYPE_LABELS[entry.type] || entry.type}
                            </span>
                          </div>
                          <p style={{ fontSize: 12, color: '#555', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {entry.description}
                          </p>
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <p style={{
                            fontSize: 14, fontWeight: 700, margin: 0,
                            color: entry.amount > 0 ? '#22c55e' : '#ef4444',
                          }}>
                            {entry.amount > 0 ? '+' : ''}₹{Math.abs(entry.amount)}
                          </p>
                          <p style={{ fontSize: 11, color: '#444', margin: 0 }}>
                            bal ₹{entry.balanceAfter}
                          </p>
                        </div>
                        <span style={{ fontSize: 11, color: '#444', flexShrink: 0, width: 60, textAlign: 'right' }}>
                          {timeAgo(entry.createdAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Activity Tab ──────────────────────────── */}
          {activeTab === 'activity' && (
            <div className="space-y-5">
              {activityLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-12 rounded-md bg-zinc-800 animate-pulse" />
                  ))}
                </div>
              ) : (
                <>
                  {/* Downloads */}
                  <div style={{ background: '#1a1a1a', border: '1px solid #222', borderRadius: 10, padding: 20 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#999', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Download style={{ width: 15, height: 15 }} /> Downloads ({downloads.length})
                    </h3>
                    {downloads.length === 0 ? (
                      <p style={{ fontSize: 13, color: '#555', textAlign: 'center', padding: 16 }}>No downloads yet</p>
                    ) : (
                      <div className="space-y-2">
                        {downloads.map((entry: any) => (
                          <div key={entry.id} style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 14px', background: '#151515', borderRadius: 8, border: '1px solid #1a1a1a',
                          }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                              background: 'rgba(255,217,61,0.12)', border: '1px solid rgba(255,217,61,0.3)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Download style={{ width: 14, height: 14, color: GOLD }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#ccc' }}>
                                {entry.action?.replace(/_/g, ' ') || 'Download'}
                              </span>
                              <p style={{ fontSize: 12, color: '#555', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {entry.description}
                              </p>
                            </div>
                            <span style={{ fontSize: 11, color: '#444', flexShrink: 0 }}>
                              {timeAgo(entry.createdAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Designs */}
                  <div style={{ background: '#1a1a1a', border: '1px solid #222', borderRadius: 10, padding: 20 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#999', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <FileText style={{ width: 15, height: 15 }} /> Designs ({designs.length})
                    </h3>
                    {designs.length === 0 ? (
                      <p style={{ fontSize: 13, color: '#555', textAlign: 'center', padding: 16 }}>No design activity yet</p>
                    ) : (
                      <div className="space-y-2">
                        {designs.map((entry: any) => (
                          <div key={entry.id} style={{
                            display: 'flex', alignItems: 'center', gap: 12,
                            padding: '10px 14px', background: '#151515', borderRadius: 8, border: '1px solid #1a1a1a',
                          }}>
                            <div style={{
                              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                              background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.3)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <FileText style={{ width: 14, height: 14, color: '#3b82f6' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#ccc' }}>
                                {entry.action?.replace(/_/g, ' ') || 'Design'}
                              </span>
                              <p style={{ fontSize: 12, color: '#555', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {entry.description}
                              </p>
                            </div>
                            <span style={{ fontSize: 11, color: '#444', flexShrink: 0 }}>
                              {timeAgo(entry.createdAt)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
          </div>
        </div>
      </PageLayout.Content>
    </PageLayout>
  );
};

export default ShowUserPage;
